import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge, type BadgeVariant } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Tabs, TabPanel, useTabs } from '@/components/ui/Tabs';
import {
  useEmailSyncAccounts,
  useConnectOutlook,
  useConnectGoogle,
  useDisconnectAccount,
  useEmailsList,
  useLinkEmail,
  type EmailSyncAccount,
  type EmailProvider,
  type EmailSyncStatus,
  type SyncedEmail,
  type EmailsListParams,
  type LinkEmailRequest,
} from './hooks/useEmailSync';

// ─── Constants ───────────────────────────────────────────────────────────────

const PROVIDER_LABELS: Record<EmailProvider, string> = {
  outlook: 'Microsoft Outlook',
  google: 'Google Workspace',
};

const STATUS_VARIANTS: Record<EmailSyncStatus, BadgeVariant> = {
  active: 'success',
  error: 'danger',
  disconnected: 'default',
};

const STATUS_LABELS: Record<EmailSyncStatus, string> = {
  active: 'Connected',
  error: 'Error',
  disconnected: 'Disconnected',
};

const LINKED_FILTER_OPTIONS = [
  { value: 'all', label: 'All Emails' },
  { value: 'linked', label: 'Linked Only' },
  { value: 'unlinked', label: 'Unlinked Only' },
];

const TABS = [
  { id: 'emails', label: 'Synced Emails' },
  { id: 'accounts', label: 'Connected Accounts' },
];

// ─── Sub-components ──────────────────────────────────────────────────────────

function AccountsList() {
  const { data: accounts, isLoading } = useEmailSyncAccounts();
  const connectOutlook = useConnectOutlook();
  const connectGoogle = useConnectGoogle();
  const disconnectAccount = useDisconnectAccount();
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);

  const handleDisconnect = useCallback(
    async (accountId: string) => {
      await disconnectAccount.mutateAsync(accountId);
      setDisconnectingId(null);
    },
    [disconnectAccount],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8" role="status">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        <span className="sr-only">Loading accounts</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Connect buttons */}
      <Card padding="md">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          Connect Email Account
        </h3>
        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
          Connect your email account to sync emails and link them to contacts and jobs.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => connectOutlook.mutate()}
            disabled={connectOutlook.isPending}
            variant="outline"
          >
            <svg
              className="mr-2 h-5 w-5"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.33.75.1.43.1.88zm-3.3 3.02L14.3 2h-5L.88 15.06h3.7zm4.73-5.65q-.42 0-.75.2-.32.2-.53.55-.2.34-.31.77-.1.43-.1.89 0 .46.1.88.1.43.31.76.21.33.53.53.33.2.76.2.42 0 .75-.2.32-.2.53-.53.2-.34.31-.76.1-.43.1-.88t-.1-.89q-.1-.43-.31-.77-.21-.34-.53-.55-.33-.2-.76-.2zM22 3.31V21l-6-3V6l6-2.69z" />
            </svg>
            {connectOutlook.isPending ? 'Connecting...' : 'Connect Outlook'}
          </Button>
          <Button
            onClick={() => connectGoogle.mutate()}
            disabled={connectGoogle.isPending}
            variant="outline"
          >
            <svg
              className="mr-2 h-5 w-5"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            {connectGoogle.isPending ? 'Connecting...' : 'Connect Google'}
          </Button>
        </div>

        {(connectOutlook.isError || connectGoogle.isError) && (
          <p className="mt-3 text-sm text-danger-600 dark:text-danger-400" role="alert">
            Failed to connect account. Please try again.
          </p>
        )}
      </Card>

      {/* Connected accounts list */}
      <Card padding="md">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          Connected Accounts
        </h3>

        {(!accounts || accounts.length === 0) && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No email accounts connected yet.
          </p>
        )}

        {accounts && accounts.length > 0 && (
          <div className="space-y-3">
            {accounts.map((account: EmailSyncAccount) => (
              <div
                key={account.id}
                className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 dark:border-gray-700"
              >
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {account.email}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {PROVIDER_LABELS[account.provider]}
                      {account.lastSyncAt && (
                        <span className="ml-2">
                          Last sync: {new Date(account.lastSyncAt).toLocaleString()}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={STATUS_VARIANTS[account.status]} size="sm">
                    {STATUS_LABELS[account.status]}
                  </Badge>
                  {account.status === 'error' && account.errorMessage && (
                    <span className="text-xs text-danger-500" title={account.errorMessage}>
                      !
                    </span>
                  )}
                  {disconnectingId === account.id ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Disconnect?</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDisconnect(account.id)}
                        disabled={disconnectAccount.isPending}
                        className="text-danger-600"
                      >
                        Yes
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDisconnectingId(null)}>
                        No
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDisconnectingId(account.id)}
                      aria-label={`Disconnect ${account.email}`}
                    >
                      Disconnect
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function LinkEmailModal({ email, onClose }: { email: SyncedEmail; onClose: () => void }) {
  const linkEmail = useLinkEmail();
  const [contactId, setContactId] = useState(email.linkedContactId ?? '');
  const [jobId, setJobId] = useState(email.linkedJobId ?? '');

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const data: LinkEmailRequest = {};
      if (contactId) data.contactId = contactId;
      if (jobId) data.jobId = jobId;
      await linkEmail.mutateAsync({ emailId: email.id, data });
      onClose();
    },
    [contactId, jobId, email.id, linkEmail, onClose],
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">
          Link &quot;{email.subject}&quot; to a contact and/or job.
        </p>
      </div>

      <div>
        <label
          htmlFor="linkContactId"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Contact ID
        </label>
        <Input
          id="linkContactId"
          placeholder="Enter contact ID to link"
          value={contactId}
          onChange={(e) => setContactId(e.target.value)}
        />
      </div>

      <div>
        <label
          htmlFor="linkJobId"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Job ID
        </label>
        <Input
          id="linkJobId"
          placeholder="Enter job ID to link"
          value={jobId}
          onChange={(e) => setJobId(e.target.value)}
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={linkEmail.isPending || (!contactId && !jobId)}>
          {linkEmail.isPending ? 'Linking...' : 'Link Email'}
        </Button>
      </div>

      {linkEmail.isError && (
        <p className="text-sm text-danger-600 dark:text-danger-400" role="alert">
          Failed to link email. Please try again.
        </p>
      )}
    </form>
  );
}

function EmailsTable() {
  const [params, setParams] = useState<EmailsListParams>({
    page: 1,
    pageSize: 25,
    search: '',
    linked: 'all',
  });
  const [linkingEmail, setLinkingEmail] = useState<SyncedEmail | null>(null);

  const { data, isLoading } = useEmailsList(params);

  return (
    <div>
      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="w-64">
          <Input
            placeholder="Search emails..."
            value={params.search}
            onChange={(e) => setParams((prev) => ({ ...prev, search: e.target.value, page: 1 }))}
          />
        </div>
        <div className="w-40">
          <Select
            label=""
            options={LINKED_FILTER_OPTIONS}
            value={params.linked ?? 'all'}
            onChange={(e) =>
              setParams((prev) => ({
                ...prev,
                linked: e.target.value as 'all' | 'linked' | 'unlinked',
                page: 1,
              }))
            }
          />
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12" role="status">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
          <span className="sr-only">Loading emails</span>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && (!data?.items || data.items.length === 0) && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <svg
            className="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          <p className="text-sm text-gray-500 dark:text-gray-400">No synced emails yet</p>
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            Connect an email account to start syncing emails
          </p>
        </div>
      )}

      {/* Emails table */}
      {data?.items && data.items.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Subject</th>
                <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">From</th>
                <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Received</th>
                <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">
                  Linked To
                </th>
                <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {data.items.map((email: SyncedEmail) => (
                <tr
                  key={email.id}
                  className="bg-white hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-800"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-medium ${
                          email.isRead
                            ? 'text-gray-700 dark:text-gray-300'
                            : 'text-gray-900 dark:text-gray-100'
                        }`}
                      >
                        {email.subject}
                      </span>
                      {email.hasAttachments && (
                        <svg
                          className="h-4 w-4 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-label="Has attachments"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                          />
                        </svg>
                      )}
                    </div>
                    {email.snippet && (
                      <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400 max-w-md">
                        {email.snippet}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{email.from}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                    {new Date(email.receivedAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    {email.linkedContactName || email.linkedJobTitle ? (
                      <div className="space-y-0.5">
                        {email.linkedContactName && (
                          <Badge variant="info" size="sm">
                            {email.linkedContactName}
                          </Badge>
                        )}
                        {email.linkedJobTitle && (
                          <Badge variant="default" size="sm">
                            {email.linkedJobTitle}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-gray-500">Not linked</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setLinkingEmail(email)}
                      aria-label={`Link email "${email.subject}"`}
                    >
                      Link
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {data && data.total > data.pageSize && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Showing {(data.page - 1) * data.pageSize + 1} to{' '}
            {Math.min(data.page * data.pageSize, data.total)} of {data.total} emails
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={data.page <= 1}
              onClick={() => setParams((prev) => ({ ...prev, page: (prev.page ?? 1) - 1 }))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={data.page * data.pageSize >= data.total}
              onClick={() => setParams((prev) => ({ ...prev, page: (prev.page ?? 1) + 1 }))}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Link Email Modal */}
      <Modal
        open={!!linkingEmail}
        onClose={() => setLinkingEmail(null)}
        title="Link Email"
        size="md"
      >
        {linkingEmail && (
          <LinkEmailModal email={linkingEmail} onClose={() => setLinkingEmail(null)} />
        )}
      </Modal>
    </div>
  );
}

// ─── Main Page Component ─────────────────────────────────────────────────────

/**
 * Email Sync page with connected accounts management and synced email viewer.
 * Supports connecting Outlook/Google accounts, viewing synced emails,
 * and linking emails to contacts and jobs.
 * Validates: Requirements 22.1, 22.2
 */
export function EmailSyncPage() {
  const { activeTab, onChange: setActiveTab } = useTabs('emails');

  return (
    <div className="flex h-full flex-col">
      {/* Page header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Email Sync</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Connect email accounts and manage synced communications
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 pt-4">
        <Tabs tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />
      </div>

      {/* Synced Emails Tab */}
      <TabPanel tabId="emails" activeTab={activeTab}>
        <div className="px-6">
          <EmailsTable />
        </div>
      </TabPanel>

      {/* Connected Accounts Tab */}
      <TabPanel tabId="accounts" activeTab={activeTab}>
        <div className="px-6">
          <AccountsList />
        </div>
      </TabPanel>
    </div>
  );
}
